import { useState } from "react";
import {
  MoreHorizontal,
  Edit2,
  Trash2,
  Eye,
  Send,
  Clock,
  MailOpen,
  Search,
} from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { StatusBadge } from "./StatusBadge";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";

export interface Campaign {
  _id: string;
  title: string;
  description?: string;
  type: string;
  status: "draft" | "scheduled" | "sending" | "sent" | "cancelled";
  subject: string;
  preheader?: string;
  targeting: {
    audienceType: string;
    recipientsCount: number;
  };
  schedule: {
    sendImmediately: boolean;
    scheduledAt?: string;
  };
  analytics: {
    sent: number;
    opened: number;
    clicked: number;
    delivered: number;
    bounced: number;
    openRate: number;
    clickRate: number;
  };
  createdBy: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  sentAt?: string;
}

interface CampaignTableProps {
  campaigns: Campaign[];
  isLoading?: boolean;
  totalCampaigns: number;
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  statusFilter: string;
  typeFilter: string;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onTypeFilterChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onAddClick?: () => void;
  onEditClick: (campaign: Campaign) => void;
  onDeleteClick: (campaign: Campaign) => void;
  onViewClick: (campaign: Campaign) => void;
  onSendClick?: (campaign: Campaign) => void;
  onCancelClick?: (campaign: Campaign) => void;
}

export function CampaignTable({
  campaigns,
  isLoading,
  totalCampaigns,
  currentPage,
  pageSize,
  searchTerm,
  statusFilter,
  typeFilter,
  onSearchChange,
  onStatusFilterChange,
  onTypeFilterChange,
  onPageChange,
  onPageSizeChange,
  onAddClick,
  onEditClick,
  onDeleteClick,
  onViewClick,
  onSendClick,
  onCancelClick,
}: CampaignTableProps) {
  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);

  return (
    <Card className="overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="flex-1 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                icon={<Search size={16} />}
              />
            </div>
            <div className="flex gap-3">
              <Select
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
                options={[
                  { value: "all", label: "All Status" },
                  { value: "draft", label: "Draft" },
                  { value: "scheduled", label: "Scheduled" },
                  { value: "sending", label: "Sending" },
                  { value: "sent", label: "Sent" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
                wrapperClassName="w-[160px]"
              />
              <Select
                value={typeFilter}
                onChange={(e) => onTypeFilterChange(e.target.value)}
                options={[
                  { value: "all", label: "All Types" },
                  { value: "promotional", label: "Promotional" },
                  { value: "educational", label: "Educational" },
                  { value: "announcement", label: "Announcement" },
                  { value: "newsletter", label: "Newsletter" },
                  { value: "welcome", label: "Welcome" },
                  { value: "onboarding", label: "Onboarding" },
                ]}
                wrapperClassName="w-[160px]"
              />
            </div>
          </div>
          {onAddClick && (
            <Button onClick={onAddClick} className="whitespace-nowrap">
              Create Campaign
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e07828]" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <MailOpen size={48} className="text-[#989797] mb-4" />
            <h3 className="text-lg font-semibold text-[#102040] mb-2">
              No campaigns found
            </h3>
            <p className="text-sm text-[#989797]">
              Create your first email campaign to get started
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#989797] uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#989797] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#989797] uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#989797] uppercase tracking-wider">
                  Created
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#989797] uppercase tracking-wider w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {campaigns.map((campaign) => (
                <tr
                  key={campaign._id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => onViewClick(campaign)}
                >
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-[#102040]">
                        {campaign.title}
                      </span>
                      <span className="text-xs text-[#989797] mt-1">
                        {campaign.subject}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <StatusBadge status={campaign.status} />
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm font-medium text-[#102040]">
                      {campaign.analytics.sent}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-[#989797]">
                      <Clock size={14} />
                      {new Date(campaign.createdAt).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        },
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div
                      className="relative flex justify-end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setActiveMenu({
                            id: campaign._id,
                            position: {
                              top: rect.bottom + 5,
                              right: window.innerWidth - rect.right,
                            },
                          });
                        }}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MoreHorizontal size={18} className="text-[#989797]" />
                      </button>

                      {activeMenu?.id === campaign._id && (
                        <TableActionMenu
                          isOpen={activeMenu.id === campaign._id}
                          onClose={() => setActiveMenu(null)}
                          position={activeMenu.position}
                        >
                          <div className="flex flex-col">
                            <button
                              onClick={() => {
                                onViewClick(campaign);
                                setActiveMenu(null);
                              }}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#102040] hover:bg-gray-50 text-left transition-colors"
                            >
                              <Eye size={16} />
                              View Details
                            </button>
                            <button
                              onClick={() => {
                                onEditClick(campaign);
                                setActiveMenu(null);
                              }}
                              disabled={
                                campaign.status === "sent" ||
                                campaign.status === "cancelled"
                              }
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#102040] hover:bg-gray-50 disabled:opacity-50 text-left transition-colors"
                            >
                              <Edit2 size={16} />
                              Edit
                            </button>
                            {campaign.status === "draft" && onSendClick && (
                              <button
                                onClick={() => {
                                  onSendClick(campaign);
                                  setActiveMenu(null);
                                }}
                                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#e07828] hover:bg-gray-50 text-left transition-colors"
                              >
                                <Send size={16} />
                                Send Now
                              </button>
                            )}
                            {campaign.status === "scheduled" &&
                              onCancelClick && (
                                <button
                                  onClick={() => {
                                    onCancelClick(campaign);
                                    setActiveMenu(null);
                                  }}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-yellow-600 hover:bg-gray-50 text-left transition-colors"
                                >
                                  <Clock size={16} />
                                  Cancel Schedule
                                </button>
                              )}
                            <button
                              onClick={() => {
                                onDeleteClick(campaign);
                                setActiveMenu(null);
                              }}
                              disabled={
                                campaign.status === "sent" ||
                                campaign.status === "sending"
                              }
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 text-left transition-colors"
                            >
                              <Trash2 size={16} />
                              Delete
                            </button>
                          </div>
                        </TableActionMenu>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm text-[#989797]">
          Showing {campaigns.length} of {totalCampaigns} campaigns
        </p>
        <div className="flex items-center gap-4">
          <Select
            value={pageSize.toString()}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
            options={[
              { value: "10", label: "10 per page" },
              { value: "20", label: "20 per page" },
              { value: "50", label: "50 per page" },
              { value: "100", label: "100 per page" },
            ]}
            wrapperClassName="w-[130px]"
          />
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-[#989797] px-2">
              Page {currentPage}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage * pageSize >= totalCampaigns}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
